import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserRole } from '@klubovna/api-client';
import { Store } from '@ngrx/store';
import { LoginRequest, User } from '@klubovna/shared-models';
import {
  selectError,
  selectIsAuthenticated,
  selectLoading,
  selectPhotoDeleting,
  selectPhotoUploading,
  selectUpdateProfileError,
  selectUpdateProfileLoading,
  selectUser,
  selectUserAdditionalRoles,
  selectUserEmail,
  selectUserFullName,
  selectUserRole,
} from './user.selectors';
import { UserState } from './user.state';
import * as UserActions from './user.actions';
import { UpdateProfileData } from './user.actions';

@Injectable({
  providedIn: 'root',
})
export class UserFacade {
  private store: Store<UserState> = inject(Store<UserState>);

  user = toSignal(this.store.select(selectUser));
  loading = toSignal(this.store.select(selectLoading), { initialValue: false });
  error = toSignal(this.store.select(selectError));
  isAuthenticated = toSignal(this.store.select(selectIsAuthenticated), { initialValue: false });
  userRole = toSignal(this.store.select(selectUserRole));
  additionalRoles = toSignal(this.store.select(selectUserAdditionalRoles), { initialValue: [] });
  userEmail = toSignal(this.store.select(selectUserEmail));
  userFullName = toSignal(this.store.select(selectUserFullName));
  requiresPasswordChange = computed(() => this.user()?.mustChangePassword ?? false);
  updatingProfile = toSignal(this.store.select(selectUpdateProfileLoading), { initialValue: false });
  updatingProfileError = toSignal(this.store.select(selectUpdateProfileError));
  uploadingPhoto = toSignal(this.store.select(selectPhotoUploading), { initialValue: false });
  deletingPhoto = toSignal(this.store.select(selectPhotoDeleting), { initialValue: false });

  isOwner = computed(() => this.userRole() === UserRole.Owner);
  isAdmin = computed(() => this.userRole() === UserRole.Admin);
  isVolunteer = computed(() => this.userRole() === UserRole.Volunteer);
  isMember = computed(() => this.userRole() === UserRole.Member);
  isAdminOrOwner = computed(() => this.isAdmin() || this.isOwner());

  login(credentials: LoginRequest): void {
    this.store.dispatch(UserActions.login({ credentials }));
  }

  logout(): void {
    this.store.dispatch(UserActions.logout());
  }

  initializeAuth(): void {
    this.store.dispatch(UserActions.initializeAuth());
  }

  validateTokenWhenNeeded(): void {
    this.store.dispatch(UserActions.validateToken());
  }

  updateAuthenticatedUser(user: User): void {
    this.store.dispatch(UserActions.validateTokenSuccess({ user }));
  }

  hasRole(requiredRoles: UserRole[]): boolean {
    const userRole = this.userRole();
    const userAdditionalRoles = this.additionalRoles();

    const hasPrimaryRole = userRole ? requiredRoles.includes(userRole) : false;
    const hasAdditionalRole = userAdditionalRoles.some(role => requiredRoles.includes(role));

    return hasPrimaryRole || hasAdditionalRole;
  }

  updateProfile(data: UpdateProfileData): void {
    this.store.dispatch(UserActions.updateProfile({ data }));
  }

  uploadProfilePhoto(file: File): void {
    this.store.dispatch(UserActions.uploadProfilePhoto({ file }));
  }

  deleteProfilePhoto(): void {
    this.store.dispatch(UserActions.deleteProfilePhoto());
  }
}
